/**
 * Event Broadcaster - Cross-tab event synchronization using BroadcastChannel
 * Ensures all tabs stay in sync by broadcasting events between them
 */

import { logger } from '../core/utils/logger'

const CHANNEL_NAME = 'ordelio-events'

interface BroadcastMessage {
  type: 'scenario-updated'
  scenarioId: string
}

class EventBroadcaster {
  private channel: BroadcastChannel | null = null
  private listeners: Array<(scenarioId: string) => void> = []

  constructor() {
    // Initialize BroadcastChannel if supported
    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel(CHANNEL_NAME)
      this.channel.onmessage = (event: MessageEvent<BroadcastMessage>) => {
        if (event.data.type === 'scenario-updated') {
          logger.info('[EventBroadcaster] 📨 Received scenario update from other tab:', event.data.scenarioId)
          this.notifyListeners(event.data.scenarioId)
        }
      }
      logger.info('[EventBroadcaster] BroadcastChannel initialized')
    } else {
      logger.warn('[EventBroadcaster] BroadcastChannel not supported in this browser')
    }
  }

  /**
   * Notify other tabs that a scenario has been updated
   * Other tabs will reload their memory state from IndexedDB
   */
  notifyScenarioUpdate(scenarioId: string): void {
    if (!this.channel) {
      return // BroadcastChannel not supported
    }

    const message: BroadcastMessage = {
      type: 'scenario-updated',
      scenarioId
    }

    try {
      this.channel.postMessage(message)
      logger.info('[EventBroadcaster] 📤 Broadcasting scenario update to other tabs:', scenarioId)
    } catch (error) {
      logger.error('[EventBroadcaster] Failed to notify scenario update:', error)
    }
  }

  /**
   * Register listener for scenario updates from other tabs
   */
  onScenarioUpdate(callback: (scenarioId: string) => void): () => void {
    this.listeners.push(callback)
    return () => {
      const index = this.listeners.indexOf(callback)
      if (index > -1) {
        this.listeners.splice(index, 1)
      }
    }
  }

  private notifyListeners(scenarioId: string): void {
    this.listeners.forEach(listener => {
      try {
        listener(scenarioId)
      } catch (error) {
        logger.error('[EventBroadcaster] Error in listener:', error)
      }
    })
  }

  /**
   * Close the BroadcastChannel (cleanup on unmount)
   */
  close(): void {
    if (this.channel) {
      this.channel.close()
      this.channel = null
    }
  }
}

export const eventBroadcaster = new EventBroadcaster()
